/**
 * Numerically stable ELLIPSE minor-grip projection.
 *
 * This is an internal implementation detail of `translateEllipseGripDrag`:
 * it keeps the translator thin while preserving the full finite binary64 DXF
 * coordinate range. No reducer, renderer, or external package imports it.
 */

const ulpBuffer = new ArrayBuffer(8);
const ulpView = new DataView(ulpBuffer);

/** @internal Bit-exact spacing at `abs(value)`; exported for boundary tests. */
export function ellipseCoordinateUlp(value: number): number {
  const magnitude = Math.abs(value);
  if (!Number.isFinite(magnitude)) return Number.POSITIVE_INFINITY;
  if (magnitude === 0) return Number.MIN_VALUE;
  ulpView.setFloat64(0, magnitude, false);
  const exponentBits = (ulpView.getUint32(0, false) >>> 20) & 0x7ff;
  if (exponentBits === 0) return Number.MIN_VALUE;
  return 2 ** (exponentBits - 1023 - 52);
}

interface BinaryScale {
  exponent: number;
  value: number;
}

interface ScaledProduct {
  coefficient: number;
  exponent: number;
}

/** A finite positive power-of-two scale, exact for every binary64 value. */
function binaryScale(value: number): BinaryScale | null {
  const magnitude = Math.abs(value);
  if (!(magnitude > 0) || !Number.isFinite(magnitude)) return null;
  ulpView.setFloat64(0, magnitude, false);
  const exponentBits = (ulpView.getUint32(0, false) >>> 20) & 0x7ff;
  if (exponentBits > 0) {
    const exponent = exponentBits - 1023;
    return { exponent, value: 2 ** exponent };
  }
  // Subnormals have no encoded leading bit. Recover the highest fraction bit;
  // correct Math.log2 if it rounds either side of that exact integer boundary.
  const units = magnitude / Number.MIN_VALUE;
  let unitExponent = Math.floor(Math.log2(units));
  if (2 ** unitExponent > units) unitExponent -= 1;
  if (2 ** (unitExponent + 1) <= units) unitExponent += 1;
  const exponent = -1074 + unitExponent;
  return { exponent, value: 2 ** exponent };
}

function scaledProduct(a: number, b: number): ScaledProduct | null {
  const aScale = binaryScale(a);
  const bScale = binaryScale(b);
  if (!aScale || !bScale) return null;
  return {
    coefficient: (a / aScale.value) * (b / bScale.value),
    exponent: aScale.exponent + bScale.exponent,
  };
}

function productAtExponent(
  product: ScaledProduct | null,
  exponent: number,
): number {
  if (!product) return 0;
  const shift = product.exponent - exponent;
  if (shift < -1074) return 0;
  if (shift > 1023) return Number.POSITIVE_INFINITY;
  return product.coefficient * 2 ** shift;
}

interface MinorGripRatioInput {
  centerX: number;
  centerY: number;
  majorX: number;
  majorY: number;
  majorZ: number;
  orientation: 1 | -1;
  worldX: number;
  worldY: number;
}

export function computeEllipseMinorGripRatio({
  centerX,
  centerY,
  majorX,
  majorY,
  majorZ,
  orientation,
  worldX,
  worldY,
}: MinorGripRatioInput): number | null {
  if (
    !Number.isFinite(centerX) ||
    !Number.isFinite(centerY) ||
    !Number.isFinite(majorX) ||
    !Number.isFinite(majorY) ||
    !Number.isFinite(majorZ) ||
    !Number.isFinite(worldX) ||
    !Number.isFinite(worldY)
  ) {
    return null;
  }
  const dx = worldX - centerX;
  const dy = worldY - centerY;
  // A finite world-minus-center subtraction may overflow. It is harmless only
  // when its determinant coefficient is exactly zero: majorY*dx or majorX*dy
  // then contributes nothing. Any relevant overflow must remain fail-closed.
  if (
    (!Number.isFinite(dx) && majorY !== 0) ||
    (!Number.isFinite(dy) && majorX !== 0)
  ) {
    return null;
  }

  const majorScale = binaryScale(Math.max(Math.abs(majorX), Math.abs(majorY)));
  if (!majorScale) return null;

  // Scale determinant products independently, then align their exponents.
  // Whole-vector scaling can lose a tiny perpendicular component beside an
  // irrelevant huge parallel offset; raw products overflow near 1e+/-300.
  const leftProduct = scaledProduct(majorX, dy);
  const rightProduct = scaledProduct(majorY, dx);
  if (!leftProduct && !rightProduct) return null;
  const areaExponent = Math.max(
    leftProduct?.exponent ?? Number.NEGATIVE_INFINITY,
    rightProduct?.exponent ?? Number.NEGATIVE_INFINITY,
  );
  const left = productAtExponent(leftProduct, areaExponent);
  const right = productAtExponent(rightProduct, areaExponent);
  const orientedArea = orientation * (left - right);

  const unitRoundoff = Number.EPSILON / 2;
  const arithmeticError =
    (3 + 16 * unitRoundoff) * unitRoundoff * (Math.abs(left) + Math.abs(right));
  const dxUncertainty =
    (ellipseCoordinateUlp(worldX) +
      ellipseCoordinateUlp(centerX) +
      ellipseCoordinateUlp(dx)) /
    2;
  const dyUncertainty =
    (ellipseCoordinateUlp(worldY) +
      ellipseCoordinateUlp(centerY) +
      ellipseCoordinateUlp(dy)) /
    2;
  const coordinateError =
    (majorX === 0
      ? 0
      : productAtExponent(
          scaledProduct(Math.abs(majorX), dyUncertainty),
          areaExponent,
        )) +
    (majorY === 0
      ? 0
      : productAtExponent(
          scaledProduct(Math.abs(majorY), dxUncertainty),
          areaExponent,
        ));
  if (!(orientedArea > arithmeticError + coordinateError)) return null;

  // Normalize both major-axis norms by exact powers of two. Raw `hypot`
  // overflows for finite multi-component vectors near Number.MAX_VALUE.
  const major3DScale = binaryScale(
    Math.max(Math.abs(majorX), Math.abs(majorY), Math.abs(majorZ)),
  );
  if (!major3DScale) return null;
  const majorMagnitude2D = Math.hypot(
    majorX / majorScale.value,
    majorY / majorScale.value,
  );
  const majorMagnitude3D = Math.hypot(
    majorX / major3DScale.value,
    majorY / major3DScale.value,
    majorZ / major3DScale.value,
  );

  // Ratio = ratioUnit * 2^(areaExp-major2DExp-major3DExp). Exponent
  // arithmetic avoids overflow/underflow and Infinity-vs-Infinity comparisons.
  const ratioUnit = orientedArea / (majorMagnitude2D * majorMagnitude3D);
  const ratioScale = binaryScale(ratioUnit);
  if (!ratioScale) return null;
  const ratioExponent =
    ratioScale.exponent +
    areaExponent -
    majorScale.exponent -
    major3DScale.exponent;
  const ratio =
    ratioExponent >= 0
      ? 1
      : (ratioUnit / ratioScale.value) * 2 ** ratioExponent;
  return ratio > 0 ? ratio : null;
}
